import type { AudioTelemetry } from "../model/types";
import type { ModulationSnapshot } from "./types";
import type { WebGLRenderer } from "./webgl-renderer";

export interface ScopeCanvases {
  oscilloscope?: HTMLCanvasElement;
  spectrum?: HTMLCanvasElement;
  image?: HTMLCanvasElement;
}

function contextFor(canvas: HTMLCanvasElement): CanvasRenderingContext2D | undefined {
  const dpr = Math.min(2, globalThis.devicePixelRatio || 1);
  const width = Math.max(1, Math.round((canvas.clientWidth || canvas.width || 320) * dpr));
  const height = Math.max(1, Math.round((canvas.clientHeight || canvas.height || 120) * dpr));
  if (canvas.width !== width) canvas.width = width;
  if (canvas.height !== height) canvas.height = height;
  return canvas.getContext("2d") ?? undefined;
}

function prepare(ctx: CanvasRenderingContext2D): void {
  const { width, height } = ctx.canvas;
  ctx.fillStyle = "#050806";
  ctx.fillRect(0, 0, width, height);
  ctx.strokeStyle = "rgba(80, 237, 137, .12)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let x = 0; x <= width; x += width / 8) { ctx.moveTo(x, 0); ctx.lineTo(x, height); }
  for (let y = 0; y <= height; y += height / 4) { ctx.moveTo(0, y); ctx.lineTo(width, y); }
  ctx.stroke();
}

export class ScopeRenderer {
  #canvases: ScopeCanvases;
  #timeData = new Float32Array(2048);
  #frequencyData = new Float32Array(1024);
  #lastUpdate = -Infinity;

  constructor(canvases: ScopeCanvases = {}) {
    this.#canvases = canvases;
  }

  setCanvases(canvases: ScopeCanvases): void {
    this.#canvases = canvases;
  }

  update(
    now: number,
    analyser: AnalyserNode | undefined,
    renderer: WebGLRenderer,
    modulation: ModulationSnapshot,
  ): AudioTelemetry | undefined {
    if (typeof document !== "undefined" && document.hidden) return undefined;
    if (now - this.#lastUpdate < 1000 / 30) return undefined;
    this.#lastUpdate = now;

    if (analyser) {
      if (this.#timeData.length !== analyser.fftSize) this.#timeData = new Float32Array(analyser.fftSize);
      if (this.#frequencyData.length !== analyser.frequencyBinCount) this.#frequencyData = new Float32Array(analyser.frequencyBinCount);
      analyser.getFloatTimeDomainData(this.#timeData);
      analyser.getFloatFrequencyData(this.#frequencyData);
    } else {
      this.#timeData.fill(0);
      this.#frequencyData.fill(-120);
    }

    this.#drawOscilloscope();
    this.#drawSpectrum(analyser?.context.sampleRate ?? 48_000);
    this.#drawImageScope(renderer);
    const band = (lowHz: number, highHz: number, sampleRate: number): number => {
      const nyquist = sampleRate / 2;
      const start = Math.max(0, Math.floor(lowHz / nyquist * this.#frequencyData.length));
      const end = Math.min(this.#frequencyData.length, Math.ceil(highHz / nyquist * this.#frequencyData.length));
      let sum = 0;
      for (let i = start; i < end; i += 1) sum += 10 ** ((this.#frequencyData[i] ?? -120) / 20);
      return end > start ? Math.min(1, sum / (end - start) * 4) : 0;
    };
    const sampleRate = analyser?.context.sampleRate ?? 48_000;
    return {
      rms: modulation.audioRms,
      peak: modulation.audioPeak,
      low: band(30, 250, sampleRate),
      mid: band(250, 2_500, sampleRate),
      high: band(2_500, 12_000, sampleRate),
      envelope: modulation.envelope,
      sequencerStep: Math.max(0, Math.min(15, Math.round(modulation.sequencer * 15))),
      sampleTime: modulation.sampleFrame,
    };
  }

  #drawOscilloscope(): void {
    const canvas = this.#canvases.oscilloscope;
    if (!canvas) return;
    const ctx = contextFor(canvas);
    if (!ctx) return;
    prepare(ctx);
    ctx.strokeStyle = "#5cf39a";
    ctx.lineWidth = Math.max(1, canvas.width / 600);
    ctx.beginPath();
    for (let i = 0; i < this.#timeData.length; i += 1) {
      const x = i / (this.#timeData.length - 1) * canvas.width;
      const y = (0.5 - (this.#timeData[i] ?? 0) * 0.42) * canvas.height;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }

  #drawSpectrum(_sampleRate: number): void {
    const canvas = this.#canvases.spectrum;
    if (!canvas) return;
    const ctx = contextFor(canvas);
    if (!ctx) return;
    prepare(ctx);
    ctx.strokeStyle = "#62d7ff";
    ctx.lineWidth = Math.max(1, canvas.width / 600);
    ctx.beginPath();
    const bins = Math.min(this.#frequencyData.length, 640);
    for (let i = 1; i < bins; i += 1) {
      const fraction = Math.log(i + 1) / Math.log(bins + 1);
      const db = this.#frequencyData[i] ?? -120;
      const x = fraction * canvas.width;
      const y = Math.max(0, Math.min(canvas.height, (-db / 120) * canvas.height));
      if (i === 1) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }

  #drawImageScope(renderer: WebGLRenderer): void {
    const canvas = this.#canvases.image;
    if (!canvas) return;
    const ctx = contextFor(canvas);
    if (!ctx) return;
    prepare(ctx);
    const width = 80;
    const height = 60;
    const pixels = renderer.readPixels(width, height);
    ctx.lineWidth = Math.max(1, canvas.width / 700);
    ctx.beginPath();
    for (let x = 0; x < width; x += 1) {
      let luma = 0;
      let chroma = 0;
      for (let y = 0; y < height; y += 1) {
        const index = (y * width + x) * 4;
        const r = (pixels[index] ?? 0) / 255;
        const g = (pixels[index + 1] ?? 0) / 255;
        const b = (pixels[index + 2] ?? 0) / 255;
        const value = r * 0.299 + g * 0.587 + b * 0.114;
        luma += value;
        chroma += Math.hypot(r - value, b - value);
      }
      luma /= height;
      chroma /= height;
      const px = x / (width - 1) * canvas.width;
      const py = (1 - luma) * canvas.height;
      if (x === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      ctx.fillStyle = "rgba(210, 106, 255, .3)";
      ctx.fillRect(px, (1 - Math.min(1, chroma)) * canvas.height, Math.max(1, canvas.width / width), 2);
    }
    ctx.strokeStyle = "#ffe77a";
    ctx.stroke();
  }
}

